import React, { useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Calendar, Loader, AlertCircle } from 'lucide-react';
import { defaultChartOptions, getMetricColor, formatMetricName } from './chartConfig';
import './ProgressChartSection.css';

const ProgressChartSection = ({ data, loading, error, elementType }) => {
  const [lookbackPeriod, setLookbackPeriod] = useState(90);

  if (loading) {
    return (
      <div className="section-loading">
        <div className="section-loading-spinner" />
        <p>Loading progress data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="section-error">
        <AlertCircle className="section-error-icon" />
        <p className="section-error-message">{error}</p>
      </div>
    );
  }

  if (!data || !data.labels || data.labels.length === 0) {
    return (
      <div className="section-no-data">
        <p>No progress data available yet. Complete more attempts to see your progress over time.</p>
      </div>
    );
  }

  // Prepare chart data
  const chartData = {
    labels: data.labels.map(label => {
      // Format date labels (e.g., "Jan 15" or "1/15")
      const date = new Date(label);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }),
    datasets: data.datasets.map(dataset => {
      const color = getMetricColor(dataset.metric);
      const metricLabel = formatMetricName(dataset.metric);
      return {
        label: metricLabel,
        data: dataset.data,
        borderColor: color.border,
        backgroundColor: color.background,
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: color.border,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        tension: 0.3,
        fill: true
      };
    })
  };

  // Determine Y-axis title based on metrics being displayed
  let yAxisTitle = 'Value';
  if (data.datasets && data.datasets.length > 0) {
    // If all datasets are the same metric, use that metric name
    const firstMetric = data.datasets[0].metric;
    const allSameMetric = data.datasets.every(d => d.metric === firstMetric);
    if (allSameMetric && firstMetric) {
      yAxisTitle = formatMetricName(firstMetric);
    } else if (data.datasets.length === 1) {
      yAxisTitle = formatMetricName(firstMetric);
    } else {
      yAxisTitle = 'Performance Metrics';
    }
  }

  // Chart options with custom configuration
  const chartOptions = {
    ...defaultChartOptions,
    plugins: {
      ...defaultChartOptions.plugins,
      title: {
        display: false
      },
      legend: {
        ...defaultChartOptions.plugins.legend,
        position: 'bottom'
      }
    },
    scales: {
      ...defaultChartOptions.scales,
      y: {
        ...defaultChartOptions.scales.y,
        beginAtZero: false,
        title: {
          display: true,
          text: yAxisTitle,
          font: {
            size: 12,
            weight: 'bold'
          }
        }
      },
      x: {
        ...defaultChartOptions.scales.x,
        title: {
          display: true,
          text: 'Date',
          font: {
            size: 12,
            weight: 'bold'
          }
        }
      }
    }
  };

  // Handle lookback period change
  const handlePeriodChange = (period) => {
    setLookbackPeriod(period);
    // In a real implementation, this would trigger a new API call
    // For now, we'll just update the state
  };

  return (
    <div className="progress-chart-section">
      {/* Controls */}
      <div className="chart-controls">
        <div className="period-selector">
          <Calendar className="period-icon" />
          <span className="period-label">Time Period:</span>
          <div className="period-buttons">
            <button
              className={`period-button ${lookbackPeriod === 7 ? 'active' : ''}`}
              onClick={() => handlePeriodChange(7)}
            >
              7 Days
            </button>
            <button
              className={`period-button ${lookbackPeriod === 30 ? 'active' : ''}`}
              onClick={() => handlePeriodChange(30)}
            >
              30 Days
            </button>
            <button
              className={`period-button ${lookbackPeriod === 90 ? 'active' : ''}`}
              onClick={() => handlePeriodChange(90)}
            >
              90 Days
            </button>
            <button
              className={`period-button ${lookbackPeriod === 365 ? 'active' : ''}`}
              onClick={() => handlePeriodChange(365)}
            >
              1 Year
            </button>
          </div>
        </div>
      </div>

      {/* Chart Container */}
      <div className="chart-container">
        <Line data={chartData} options={chartOptions} />
      </div>

      {/* Data Summary */}
      {data.summary && (
        <div className="chart-summary">
          <div className="summary-stats">
            <div className="summary-stat">
              <span className="stat-label">Total Attempts:</span>
              <span className="stat-value">{data.summary.total_attempts || data.labels.length}</span>
            </div>
            <div className="summary-stat">
              <span className="stat-label">Date Range:</span>
              <span className="stat-value">
                {data.labels.length > 0 && (
                  <>
                    {new Date(data.labels[0]).toLocaleDateString()} - {new Date(data.labels[data.labels.length - 1]).toLocaleDateString()}
                  </>
                )}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Missing Data Notice */}
      {data.has_gaps && (
        <div className="data-notice">
          <AlertCircle className="notice-icon" />
          <span className="notice-text">
            Some data points may be missing. Chart shows available data only.
          </span>
        </div>
      )}
    </div>
  );
};

export default ProgressChartSection;
